import React, { useMemo } from "react";
import {
  Document,
  Page,
  Text,
  View,
  Link,
  Image as PdfImage,
  StyleSheet,
  PDFDownloadLink,
  Font,
} from "@react-pdf/renderer";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import { stripEmojis } from "./pdfTextUtils";
import { getTableCellText, getTableColumnWidths } from "./exportTableLayout";
import type {
  Root,
  Content,
  PhrasingContent,
  Paragraph,
  Heading,
  List,
  ListItem,
  Blockquote,
  Code,
  ThematicBreak,
  Table as MdTable,
  TableRow,
  TableCell,
  Image as MdImage,
  Link as MdLink,
  Text as MdText,
} from "mdast";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";

import logo from "@/assets/logo.png";
import mathRegular from "katex/dist/fonts/KaTeX_Main-Regular.ttf";
import mathBold from "katex/dist/fonts/KaTeX_Main-Bold.ttf";
import mathItalic from "katex/dist/fonts/KaTeX_Main-Italic.ttf";
import mathBoldItalic from "katex/dist/fonts/KaTeX_Main-BoldItalic.ttf";
import mathSymbols from "katex/dist/fonts/KaTeX_AMS-Regular.ttf";
import unicodeFallback from "@/assets/fonts/NotoSans-Regular.ttf";

// Reuse the app's bundled math fonts for symbols absent from the PDF base fonts.
Font.register({ family: "ExportMath", fonts: [
  { src: mathRegular, fontWeight: 400 },
  { src: mathBold, fontWeight: 700 },
  { src: mathItalic, fontStyle: "italic", fontWeight: 400 },
  { src: mathBoldItalic, fontStyle: "italic", fontWeight: 700 },
] });
Font.register({ family: "ExportSymbols", fonts: [
  { src: mathSymbols, fontWeight: 400 },
  { src: mathSymbols, fontStyle: "italic", fontWeight: 400 },
] });
Font.register({ family: "ExportUnicode", fonts: [
  { src: unicodeFallback, fontWeight: 400 },
  { src: unicodeFallback, fontStyle: "italic", fontWeight: 400 },
] });

// Allow emergency breaks, but strongly prefer spaces. The renderer supports
// hyphenationPenalty at runtime although it is missing from its TextProps.
const tableTextWrapping = {
  hyphenationCallback: (word: string) => Array.from(word),
  hyphenationPenalty: 10000,
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 64,
    paddingBottom: 64,
    paddingHorizontal: 54,
    fontSize: 10.5,
    // Inherited lineHeight is repeatedly multiplied during pagination in this renderer.
    fontFamily: ["Helvetica", "ExportMath", "ExportSymbols", "ExportUnicode"],
  },
  header: {
    flexDirection: "row",
    position: "absolute",
    top: 24,
    left: 54,
    right: 54,
    fontSize: 10,
    color: "#666",
  },
  headerText: {
    paddingTop: '7px',
  },
  footer: {
    position: "absolute",
    bottom: 24,
    left: 54,
    right: 54,
    fontSize: 10,
    color: "#666",
    textAlign: "right",
  },
  h1: { fontSize: 18, marginTop: 12, marginBottom: 6, fontWeight: 700, color: "#1F2937" },
  h2: { fontSize: 14, marginTop: 11, marginBottom: 6, fontWeight: 700, color: "#1F2937" },
  h3: { fontSize: 12, marginTop: 10, marginBottom: 5, fontWeight: 700, color: "#334155" },
  h4: { fontSize: 11, marginTop: 9, marginBottom: 5, fontWeight: 700, color: "#334155" },
  h5: { fontSize: 11, marginTop: 8, marginBottom: 5, fontWeight: 700, color: "#334155" },
  h6: { fontSize: 11, marginTop: 8, marginBottom: 5, fontWeight: 700, color: "#334155" },
  p: { marginBottom: 6 },
  liRow: { flexDirection: "row", marginBottom: 3 },
  liBullet: { width: 16 },
  liContent: { flex: 1 },
  codeBlock: {
    fontFamily: ["Courier", "ExportMath", "ExportSymbols", "ExportUnicode"],
    fontSize: 9,
    backgroundColor: "#f8fafc",
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderRadius: 6,
    padding: 8,
    marginVertical: 6,
  },
  inlineCode: {
    fontFamily: ["Courier", "ExportMath", "ExportSymbols", "ExportUnicode"],
    fontSize: 9,
    backgroundColor: "#f8fafc",
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderRadius: 3,
    paddingHorizontal: 3,
  },
  blockquote: {
    borderLeftWidth: 3,
    borderLeftColor: "#94a3b8",
    paddingLeft: 8,
    color: "#475569",
    marginVertical: 6,
  },
  hr: { borderBottomWidth: 1, borderBottomColor: "#e5e7eb", marginVertical: 8 },
  table: {
    width: "100%",
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "#cbd5e1",
    marginVertical: 8,
  },
  tableRow: { flexDirection: "row", width: "100%" },
  tableHeaderRow: { backgroundColor: "#f1f5f9" },
  tableAlternateRow: { backgroundColor: "#f8fafc" },
  tableCell: {
    borderStyle: "solid",
    borderColor: "#cbd5e1",
    borderTopWidth: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    paddingVertical: 6,
    paddingHorizontal: 8,
    flexShrink: 0,
    minWidth: 0,
  },
  tableCellText: { fontSize: 10, color: "#334155" },
  img: { marginVertical: 6, maxWidth: "100%" },
  citations: {
    marginTop: 20,
    borderTopWidth: 1,
    borderTopColor: "#cbd5e1",
    paddingTop: 8,
  },
  citationHeader: {
    fontSize: 10,
    fontWeight: 700,
    marginBottom: 4,
    color: "#334155",
  },
  citation: {
    fontSize: 9,
    color: "#475569",
    marginBottom: 3,
  },
});

// ---------- Inline (phrasing) renderer ----------
function Inline({ children }: { children: PhrasingContent[] }) {
  return (
    <>
      {children.map((node, i) => renderInlineNode(node, i))}
    </>
  );
}

function renderInlineNode(node: PhrasingContent, key: number): React.ReactNode {
  switch (node.type) {
    case "text":
      return <Text key={key}>{(node as MdText).value}</Text>;
    case "emphasis":
      return <Text key={key} style={{ fontStyle: "italic" }}>
        <Inline children={node.children} />
      </Text>;
    case "strong":
      return <Text key={key} style={{ fontWeight: 700 }}>
        <Inline children={node.children} />
      </Text>;
    case "delete":
      return <Text key={key} style={{ textDecoration: "line-through" }}>
        <Inline children={node.children} />
      </Text>;
    case "inlineCode":
      return <Text key={key} style={styles.inlineCode}>{node.value}</Text>;
    case "break":
      return <Text key={key}>{"\n"}</Text>;
    case "link": {
      const ln = node as MdLink;
      return (
        <Link key={key} src={ln.url}>
          <Inline children={ln.children} />
        </Link>
      );
    }
    case "image": {
      // Images can't live inside <Text>. Emit as a block fragment.
      const img = node as MdImage;
      return <PdfImage key={key} src={img.url} style={styles.img} />;
    }
    default:
      return null;
  }
}

// ---------- Block renderer ----------
function estimateTableRowHeight(row: TableRow, widths: number[]): number {
  // Use a generous glyph-width/leading bound only to decide if a row can stay whole.
  // Truly tall rows must remain breakable so the renderer can continue their content.
  const lines = row.children.map((cell, index) =>
    getTableCellText(cell).split(/\r?\n/).reduce((count, line) =>
      count + Math.max(1, Math.ceil(line.length * 10 / Math.max(1, widths[index] - 17))), 0),
  );
  return 13 + 14 * Math.max(1, ...lines);
}

function Block({ node, availableWidth = 504 }: { node: Content; availableWidth?: number }) {
  switch (node.type) {
    case "heading": {
      const h = node as Heading;
      const style =
        styles[`h${Math.min(h.depth, 4)}` as keyof typeof styles] || styles.h4;
      return (
        <Text key={(node as any).position?.start?.offset ?? Math.random()} style={style} minPresenceAhead={24}>
          <Inline children={h.children as PhrasingContent[]} />
        </Text>
      );
    }
    case "paragraph": {
      const p = node as Paragraph;
      // Keep all inline content in one Text node so markdown fragments flow
      // together instead of wrapping each fragment onto its own line.
      if (!p.children.some(child => child.type === "image")) {
        return (
          <Text key={(node as any).position?.start?.offset ?? Math.random()} style={styles.p}>
            <Inline children={p.children as PhrasingContent[]} />
          </Text>
        );
      }

      // Images cannot live inside Text, so retain a mixed-content fallback.
      return (
        <View key={(node as any).position?.start?.offset ?? Math.random()} style={styles.p}>
          {p.children.map((child, i) =>
            child.type === "image"
              ? <PdfImage key={i} src={(child as MdImage).url} style={styles.img} />
              : <Text key={i}><Inline children={[child as PhrasingContent]} /></Text>
          )}
        </View>
      );
    }
    case "list": {
      const l = node as List;
      return (
        <View key={(node as any).position?.start?.offset ?? Math.random()} style={{ marginVertical: 2 }}>
          {l.children.map((item, idx) => (
            <ListItemBlock
              key={idx}
              item={item as ListItem}
              index={l.ordered ? (l.start ?? 1) + idx : undefined}
              ordered={!!l.ordered}
              availableWidth={availableWidth}
            />
          ))}
        </View>
      );
    }
    case "blockquote": {
      const bq = node as Blockquote;
      return (
        <View key={(node as any).position?.start?.offset ?? Math.random()} style={styles.blockquote}>
          {bq.children.map((child, i) => <Block key={i} node={child} availableWidth={Math.max(1, availableWidth - 11)} />)}
        </View>
      );
    }
    case "code": {
      const c = node as Code;
      return (
        <View key={(node as any).position?.start?.offset ?? Math.random()} style={styles.codeBlock}>
          <Text>{c.value}</Text>
        </View>
      );
    }
    case "thematicBreak":
      return <View key={(node as any).position?.start?.offset ?? Math.random()} style={styles.hr} />;
    case "table": {
      const t = node as MdTable;
      const columnCount = t.children[0]?.children.length ?? 0;
      if (columnCount === 0) return null;
      const tableWidth = Math.max(1, availableWidth - 1);
      const columnWidths = getTableColumnWidths(t, tableWidth);
      const rowHeights = t.children.map(row => estimateTableRowHeight(row, columnWidths));
      const firstRowHeight = rowHeights[1] ?? 0;
      const headerPresence = Math.min(
        firstRowHeight > 640 ? 28 : firstRowHeight,
        Math.max(0, 640 - rowHeights[0]),
      );
      return (
        <View key={(node as any).position?.start?.offset ?? Math.random()} style={styles.table}>
          {t.children.map((row, rowIndex) => (
            <View
              key={rowIndex}
              wrap={rowHeights[rowIndex] > 640}
              style={[
                styles.tableRow,
                rowIndex === 0 ? styles.tableHeaderRow : rowIndex % 2 === 0 ? styles.tableAlternateRow : {},
              ]}
              minPresenceAhead={rowIndex === 0 ? headerPresence : undefined}
            >
              {(row as TableRow).children.map((cell, columnIndex) => {
                const markdownAlignment = t.align?.[columnIndex];
                const textAlign = markdownAlignment === "center"
                  ? "center"
                  : markdownAlignment === "right"
                    ? "right"
                    : "left";
                const isHeader = rowIndex === 0;
                return (
                  <View
                    key={columnIndex}
                    style={[
                      styles.tableCell,
                      { width: `${columnWidths[columnIndex] / Math.round(tableWidth) * 100}%` },
                      columnIndex > 0 ? { borderLeftWidth: 0.5 } : {},
                      rowIndex > 0 ? { borderTopWidth: 0.5 } : {},
                    ]}
                  >
                    <Text
                      {...tableTextWrapping}
                      style={[
                        styles.tableCellText,
                        { textAlign },
                        isHeader ? { fontWeight: 700, color: "#334155" } : {},
                      ]}
                    >
                      <Inline children={(cell as TableCell).children as PhrasingContent[]} />
                    </Text>
                  </View>
                );
              })}
            </View>
          ))}
        </View>
      );
    }
    case "image": {
      const img = node as MdImage;
      return <PdfImage key={(node as any).position?.start?.offset ?? Math.random()} src={img.url} style={styles.img} />;
    }
    default:
      return null; // ignore html, definitions, etc. in client-only flow
  }
}

function ListItemBlock({ item, ordered, index, availableWidth }: { item: ListItem; ordered: boolean; index?: number; availableWidth: number }) {
  // Render only the first paragraph of the list item inline, then any nested blocks below it
  const [first, ...rest] = item.children;
  return (
    <View style={styles.liRow}>
      <Text style={styles.liBullet}>{ordered ? `${index}.` : "•"}</Text>
      <View style={styles.liContent}>
        {first ? <Block node={first} availableWidth={Math.max(1, availableWidth - 16)} /> : null}
        {rest.map((child, i) => <Block key={i} node={child} availableWidth={Math.max(1, availableWidth - 16)} />)}
      </View>
    </View>
  );
}

type Props = {
  markdown: string;
  citations: any[];
  fileName?: string;
  title?: string;
};

type PdfDocumentProps = Pick<Props, "markdown" | "citations" | "title">;

export function MarkdownPdfDocument({
  markdown,
  citations = [],
  title = "ALLY",
}: PdfDocumentProps) {
  const tree = useMemo<Root>(() => {
    const u = unified().use(remarkParse).use(remarkGfm);
    return u.parse(stripEmojis(markdown)) as Root;
  }, [markdown]);

  const Doc = (
    <Document title={title}>
      <Page size={"LETTER"} style={styles.page} wrap>
        {/* Header */}
        <View fixed style={styles.header}>
          <PdfImage src={logo} style={{ width: 48, height: 24, marginRight: 8 }} />
        </View>

        {/* Content */}
        {tree.children.map((node, i) => (
          <Block key={i} node={node as Content} />
        ))}

        {/* Citations */}
        {citations.length > 0 && (
          <View style={styles.citations}>
            <Text style={styles.citationHeader}>Citations</Text>
            {citations.map(c => (
              <Text key={c.id} style={styles.citation}>
                [{c.id}] {stripEmojis(String(c.title))}
              </Text>
            ))}
          </View>
        )}

        {/* Footer with page numbers */}
        <Text
          fixed
          style={styles.footer}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
        />
      </Page>
    </Document>
  );

  return Doc;
}

export default function MarkdownPdfClient({
  markdown,
  citations = [],
  fileName = "document.pdf",
  title = "ALLY",
}: Props) {
  const stripEmojisAndMarkdown = (text: string) => {
    let result = text.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF]|[\u2011-\u26FF]|[\uFE00-\uFE0F])/g, "");
    result = result.replace(/[*_#\[\]\(\)~`>+\-=|{}!]/g, "");
    return result;
  };

  return (
    <PDFDownloadLink
      document={<MarkdownPdfDocument markdown={markdown} citations={citations} title={title} />}
      fileName={stripEmojisAndMarkdown(fileName)}
    >
      <button className="flex items-center gap-3 hover:bg-neutral rounded-lg cursor-pointer dark:text-white w-full py-1 px-3">
        <FontAwesomeIcon className="w-[24px]" icon={faFilePdf} size="sm" />
        <div className="select-none text-sm">PDF</div>
      </button>
    </PDFDownloadLink>
  );
}
